import { yupResolver } from "@hookform/resolvers/yup";
import {Alert, Box, Typography} from "@mui/material";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { ServiceDataForm } from "@/pages/petAndBeauty/Comercial/ServicesPages/ServiceForm/ServiceDataForm";
import { DynamicPriceForm } from "@/pages/petAndBeauty/Comercial/ServicesPages/ServiceForm/DynamicPriceForm";
import { EmployeeSelectionForm } from "@/pages/petAndBeauty/Comercial/ServicesPages/ServiceForm/EmployeeSelectionForm";
import { GenericImageSelector, ProcessedImage } from "@/components/general/Images/GenericImageSelector";
import ComercialDrawer from "../../../../../components/pet-beauty/CormercialDrawer";
import { PrimaryButton } from "../../../../../components/general/buttons/PrimaryButton";
import { SecondaryButton } from "../../../../../components/general/buttons/SecondaryButton";
import { LeftMarginBackButton } from "@/components/general/buttons/BackPageButton";
import serviceEmployeeApi from "../../../../../services/api/ServiceEmployee";
import serviceStoreApi from "../../../../../services/api/ServiceStore";
import { IServiceStore } from "../../../../../services/api/ServiceStore/types";
import { createServiceEmployee, validateServiceStorePrices } from "../usualFunctions";
import { editServiceSchema } from "@/utils/yupSchemas";
import { sharedStyles } from "../../../../../styles/shared";
import { getLocalStorageItem } from "../../../../../hooks/localStorageHooks";
import useFetchEmployeesFromStore from "../../../../../hooks/entities/useFetchEmployeesFromStore";
import ImageGalleryManagerApi from "@/components/general/Images/ImageGalleryManagerApi";

const EditService = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const selectedServiceEmployeeEmployeeIds: string[] = getLocalStorageItem("selectedServiceEmployeeEmployeeIds") || [];
  const selectedServiceStore: IServiceStore = getLocalStorageItem("selectedServiceStore");

  const initialPrice = useMemo(() => (selectedServiceStore?.price || 0) / 100, [selectedServiceStore]);
  const initialPriceDay = useMemo(() => (selectedServiceStore?.priceDay || []).map((p) => p / 100), [selectedServiceStore]);

  const [selectedNewImages, setSelectedNewImages] = useState<ProcessedImage[]>([]);

  const [selectedImages, setSelectedImages] = useState<ProcessedImage[]>([]);

  const refs = {
    serviceName: useRef<HTMLInputElement>(null),
    description: useRef<HTMLInputElement>(null),
  };

  const [dynamicPriceAllowed, setDynamicPriceAllowed] = useState<boolean>(
      !initialPriceDay.every((price) => price === initialPriceDay[0])
  );
  const [fixedPrice, setFixedPrice] = useState<number | null>(initialPrice);

  const [dynamicPrices, setDynamicPrices] = useState<(number | null)[]>(initialPriceDay);
  const [durationOfService, setDurationOfService] = useState<number>(selectedServiceStore?.durationOfService || 1);

  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>(selectedServiceEmployeeEmployeeIds);
  const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(false);
  const [submittedOnce, setSubmittedOnce] = useState<boolean>(false);
  const [generalErrorMessage, setGeneralErrorMessage] = useState<string>("");

  const { employees } = useFetchEmployeesFromStore(selectedServiceStore?.storeId);
  const noEmployeesSelected = selectedEmployeeIds.length === 0;

  const handleImageChange = useCallback((images: ProcessedImage[]) => {
    setSelectedImages(images);
  }, []);

  const checkIfPricesAreValid = () =>
      validateServiceStorePrices(
          fixedPrice,
          dynamicPrices[1], // Segunda
          dynamicPrices[2], // Terça
          dynamicPrices[3], // Quarta
          dynamicPrices[4], // Quinta
          dynamicPrices[5], // Sexta
          dynamicPrices[6], // Sábado
          dynamicPrices[0], // Domingo
          dynamicPriceAllowed
      );

  const {
    register,
    handleSubmit: onSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(editServiceSchema) });

  const handleUpdateServiceEmployee = async () => {
    try {
      const currentServiceEmployees = await serviceEmployeeApi.getServiceEmployeeByServiceStoreId(selectedServiceStore.id);

      for (const item of currentServiceEmployees) {
        await serviceEmployeeApi.deleteServiceEmployee(item.id);
      }

      for (const employeeId of selectedEmployeeIds) {
        await createServiceEmployee(employeeId, selectedServiceStore.id);
      }
    } catch (error) {
      console.warn("Error updating service-employee: ", error);
    }
  };

  const handleServiceAndServiceStoreEdition = async () => {
    setGeneralErrorMessage("");
    if (!submittedOnce) setSubmittedOnce(true);

    if (checkIfPricesAreValid() && !noEmployeesSelected) {
      setButtonsDisabled(true);
      await handleUpdateServiceEmployee();

      try {
        const maximumWeekPrice = Math.max(...dynamicPrices.map((p) => p || 0));
        const serviceName = refs.serviceName.current?.value || "";

        const updatedServiceStoreData = {
          serviceName,
          description: refs.description.current?.value || "",
          price: !dynamicPriceAllowed ? fixedPrice : maximumWeekPrice,
          priceDay: dynamicPriceAllowed ? dynamicPrices : Array(7).fill(fixedPrice),
          durationOfService,
        };

        const formData = new FormData();
        formData.append("serviceStore", JSON.stringify(updatedServiceStoreData));

        selectedImages.forEach((img, index) => {
          formData.append("images", img.resizedFull, `image_${index}.jpg`);
        });

        await serviceStoreApi.updateServiceStore(selectedServiceStore.id, formData);

        window.alert(t("servicePage.management.edit.success", { name: selectedServiceStore.Service.name }));
        navigate("/services");
      } catch (error: any) {
        console.error("Error editing a service or services store: ", error);
        setGeneralErrorMessage(error.response?.data?.message || t("servicePage.management.edit.unknownError"));
      } finally {
        setButtonsDisabled(false);
      }
    }
  };

  const renderEditServicePageContent = (
      <>
        <LeftMarginBackButton />

        <form onSubmit={onSubmit(handleServiceAndServiceStoreEdition)} style={sharedStyles.form}>
          <ServiceDataForm
              register={register}
              errors={errors}
              refs={refs}
              selectedServiceStore={selectedServiceStore}
              durationOfService={durationOfService}
              setDurationOfService={setDurationOfService}
              fixedPrice={fixedPrice}
              setFixedPrice={setFixedPrice}
              dynamicPriceAllowed={dynamicPriceAllowed}
              setDynamicPriceAllowed={setDynamicPriceAllowed}
              pricesAreValid={checkIfPricesAreValid()}
              submittedOnce={submittedOnce}
          />

          <DynamicPriceForm
              dynamicPriceAllowed={dynamicPriceAllowed}
              pricesAreValid={checkIfPricesAreValid()}
              submittedOnce={submittedOnce}
              dynamicPrices={dynamicPrices}
              setDynamicPrices={setDynamicPrices}
          />


          {selectedServiceStore?.id && (
              <Box sx={{ width: "100%", maxWidth: 800, mx: "auto", mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}>
                  Gerenciar Imagens Salvas (Clique na lixeira para remover permanentemente)
                </Typography>
                <ImageGalleryManagerApi images={selectedServiceStore.images ?? []} />
              </Box>
          )}

          <GenericImageSelector
              fullWidth={800}
              fullHeight={600}
              dropzoneText="Adicione novas fotos ao serviço ou arraste-as aqui"
              onChangeImages={handleImageChange}
          />

          <EmployeeSelectionForm
              employees={employees}
              selectedEmployeeIds={selectedEmployeeIds}
              setSelectedEmployeeIds={setSelectedEmployeeIds}
              noEmployeesSelected={noEmployeesSelected && submittedOnce}
          />

          {generalErrorMessage !== "" && (
              <Alert severity="error">{generalErrorMessage}</Alert>
          )}

          <Box sx={sharedStyles.dividedFormContainer}>
            <PrimaryButton
                label={t("servicePage.management.edit.confirm")}
                isLoading={buttonsDisabled}
                isDisabled={buttonsDisabled}
                icon={<EditIcon />}
            />
            <SecondaryButton
                label={t("servicePage.management.edit.cancel")}
                onClickAction={() => navigate("/services")}
                isDisabled={buttonsDisabled}
            />
          </Box>
        </form>
      </>
  );

  return (
      <Box sx={sharedStyles.sharedMainContainer}>
        <ComercialDrawer content={renderEditServicePageContent} />
      </Box>
  );
};

export default EditService;
